// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Loader2,
  Info,
  ClipboardList,
  XCircle,
  Ban,
  ArrowRightCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import type {
  EventData,
  EventDivisionSettings,
  EventTeamTracking,
  TrackingType,
  TrackingStatus,
  TrackingPaymentStatus,
} from "@/types/events";

interface Props {
  event: EventData;
  eventId: string | undefined;
  divisionSettings: EventDivisionSettings[];
}

interface CatalogRow {
  id: string;
  label: string;
}
interface MasterTeam {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  logo_url: string | null;
}
interface SearchedTeam {
  id: string;
  canonical_name: string | null;
  display_name: string | null;
  stable_slug: string | null;
  age_group: string | null;
  gender: string | null;
  home_state: string | null;
  primary_coach_name: string | null;
  club_id: string | null;
  ownership_status: string | null;
  name_sim: number;
}

const TYPE_META: Record<
  TrackingType,
  { label: string; cls: string; chip: string }
> = {
  comp: { label: "Comp", cls: "bg-emerald-100 text-emerald-800", chip: "bg-emerald-500" },
  pay_at_door: { label: "Pay at door", cls: "bg-amber-100 text-amber-800", chip: "bg-amber-500" },
  invited: { label: "Invited", cls: "bg-blue-100 text-blue-800", chip: "bg-blue-500" },
  partner: { label: "Partner", cls: "bg-purple-100 text-purple-800", chip: "bg-purple-500" },
  sponsor: { label: "Sponsor", cls: "bg-yellow-100 text-yellow-900", chip: "bg-yellow-500" },
  staff: { label: "Staff", cls: "bg-gray-200 text-gray-800", chip: "bg-gray-500" },
  other: { label: "Other", cls: "bg-slate-100 text-slate-700", chip: "bg-slate-400" },
};

const STATUS_META: Record<TrackingStatus, { label: string; cls: string }> = {
  tentative: { label: "Tentative", cls: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-100 text-emerald-800" },
  no_show: { label: "No-show", cls: "bg-red-100 text-red-800" },
  cancelled: { label: "Cancelled", cls: "bg-gray-200 text-gray-700" },
  converted: { label: "Converted", cls: "bg-blue-100 text-blue-800" },
};

const TRACKING_TYPES: TrackingType[] = [
  "comp",
  "pay_at_door",
  "invited",
  "partner",
  "sponsor",
  "staff",
  "other",
];

function paymentStatusFromType(t: TrackingType): TrackingPaymentStatus {
  if (t === "comp") return "not_required";
  if (t === "pay_at_door" || t === "invited" || t === "partner") return "pending";
  return "pending";
}

function divisionOptions(
  divisionSettings: EventDivisionSettings[],
  catalog: CatalogRow[]
) {
  const opts: { value: string; label: string; catalog_division_id: string; gender: "boys" | "girls" }[] = [];
  for (const d of divisionSettings) {
    if (!d.is_active) continue;
    const cat = catalog.find((c) => c.id === d.catalog_division_id);
    if (!cat) continue;
    if (d.has_boys)
      opts.push({
        value: `${d.catalog_division_id}:boys`,
        label: `${cat.label} Boys`,
        catalog_division_id: d.catalog_division_id,
        gender: "boys",
      });
    if (d.has_girls)
      opts.push({
        value: `${d.catalog_division_id}:girls`,
        label: `${cat.label} Girls`,
        catalog_division_id: d.catalog_division_id,
        gender: "girls",
      });
  }
  return opts;
}

const emptyDraft = (): Partial<EventTeamTracking> => ({
  team_name: "",
  master_team_id: null,
  catalog_division_id: null,
  division_label: null,
  gender: null,
  tracking_type: "pay_at_door",
  expected_amount: 0,
  amount_received: 0,
  payment_status: "pending",
  payment_method: null,
  payment_notes: null,
  contact_name: null,
  contact_email: null,
  contact_phone: null,
  expected_roster_size: 12,
  notes: null,
  internal_notes: null,
  include_in_schedule: true,
  status: "tentative",
});

export function TrackingSection({ event, eventId, divisionSettings }: Props) {
  const [rows, setRows] = useState<EventTeamTracking[]>([]);
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"all" | TrackingStatus>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | TrackingType>("all");
  const [divisionFilter, setDivisionFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<EventTeamTracking> | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Quick-add inline state
  const [qaName, setQaName] = useState("");
  const [qaDivision, setQaDivision] = useState("");
  const [qaType, setQaType] = useState<TrackingType>("pay_at_door");

  const sportId = event.sport_id;
  const orgId = event.organization_id;

  // Load catalog
  useEffect(() => {
    if (!sportId || !orgId) return;
    (async () => {
      const { data } = await supabase
        .from("division_catalog")
        .select("id, label")
        .eq("sport_id", sportId)
        .eq("organization_id", orgId)
        .eq("is_active", true)
        .order("display_order");
      setCatalog(data ?? []);
    })();
  }, [sportId, orgId]);

  // Load tracked rows
  const reload = async () => {
    if (!eventId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("event_team_tracking")
      .select("*")
      .eq("event_id", eventId)
      .order("created_at", { ascending: false });
    setLoading(false);
    if (error) {
      toast.error("Failed to load tracked teams");
      return;
    }
    setRows((data ?? []) as EventTeamTracking[]);
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const divOpts = useMemo(
    () => divisionOptions(divisionSettings, catalog),
    [divisionSettings, catalog]
  );

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (typeFilter !== "all" && r.tracking_type !== typeFilter) return false;
      if (divisionFilter !== "all" && r.catalog_division_id !== divisionFilter) return false;
      if (search.trim() && !r.team_name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, statusFilter, typeFilter, divisionFilter, search]);

  const stats = useMemo(() => {
    const tracked = rows.length;
    const confirmed = rows.filter((r) => r.status === "confirmed").length;
    const pending = rows
      .filter(
        (r) =>
          r.tracking_type === "pay_at_door" &&
          (r.payment_status === "pending" || r.payment_status === "partial")
      )
      .reduce((sum, r) => sum + (Number(r.expected_amount) - Number(r.amount_received || 0)), 0);
    const noShows = rows.filter((r) => r.status === "no_show").length;
    return { tracked, confirmed, pending, noShows };
  }, [rows]);

  const handleSave = async (draft: Partial<EventTeamTracking>) => {
    if (!eventId) {
      toast.error("Save the event first");
      return;
    }
    if (!draft.team_name?.trim()) {
      toast.error("Team name is required");
      return;
    }
    if (!draft.catalog_division_id && !draft.division_label?.trim()) {
      toast.error("Division is required");
      return;
    }
    // New squad created under a club: create the master team record first so the
    // variation (short_name) and built display_name are persisted to teams.
    let masterTeamId = draft.master_team_id || null;
    let clubId = draft.club_id || null;
    const newClubName = ((draft as any)._newClubName || "").trim();

    // Brand-new club: create the club record (base program name only — never an
    // age group or variation) before creating the squad under it.
    if (!draft.id && !clubId && newClubName) {
      if (
        /\d{1,2}\s*-?\s*u\b/i.test(newClubName) ||
        /\bunder\b/i.test(newClubName)
      ) {
        toast.error("Club name must not contain an age group or variation");
        return;
      }
      const clubSlug =
        newClubName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
          .slice(0, 60) +
        "-" +
        Math.random().toString(36).slice(2, 7);
      const clubRes = await supabase
        .from("clubs")
        .insert({
          name: newClubName,
          slug: clubSlug,
        })
        .select("id")
        .single();
      if (clubRes.error) {
        toast.error("Failed to create club: " + clubRes.error.message);
        return;
      }
      clubId = clubRes.data.id;
    }

    // Create the squad/team when adding a new squad (existing or new club). For a
    // new club we always create the team; for an existing club we only create one
    // when a variation/new squad is being defined.
    const creatingSquad =
      !draft.id && !masterTeamId && (!!newClubName || !!draft.short_name?.trim());
    if (creatingSquad) {
      const fullName = draft.team_name.trim();
      const ageGroup = (draft.division_label || "")
        .replace(/\b(boys|girls|men|women|coed)\b/gi, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
      const slug =
        fullName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
          .slice(0, 60) +
        "-" +
        Math.random().toString(36).slice(2, 7);
      const teamRes = await supabase
        .from("teams")
        .insert({
          club_id: clubId || null,
          canonical_name: fullName,
          display_name: fullName,
          short_name: draft.short_name?.trim() || null,
          stable_slug: slug,
          age_group: ageGroup || null,
          gender: draft.gender || null,
          status: "active",
        })
        .select("id")
        .single();
      if (teamRes.error) {
        toast.error("Failed to create squad: " + teamRes.error.message);
        return;
      }
      masterTeamId = teamRes.data.id;
    }

    const payload: any = {
      event_id: eventId,
      master_team_id: masterTeamId,
      club_id: clubId || null,
      team_name: draft.team_name.trim(),
      catalog_division_id: draft.catalog_division_id || null,
      division_label: draft.division_label,
      gender: draft.gender,
      tracking_type: draft.tracking_type,
      expected_amount: Number(draft.expected_amount || 0),
      amount_received: Number(draft.amount_received || 0),
      payment_status: draft.payment_status,
      payment_method: draft.payment_method || null,
      payment_notes: draft.payment_notes || null,
      contact_name: draft.contact_name || null,
      contact_email: draft.contact_email || null,
      contact_phone: draft.contact_phone || null,
      expected_roster_size: draft.expected_roster_size ?? null,
      notes: draft.notes || null,
      internal_notes: draft.internal_notes || null,
      include_in_schedule: draft.include_in_schedule ?? true,
      status: draft.status || "tentative",
    };

    let res;
    if (draft.id) {
      res = await supabase
        .from("event_team_tracking")
        .update(payload)
        .eq("id", draft.id);
    } else {
      res = await supabase.from("event_team_tracking").insert(payload);
    }
    if (res.error) {
      toast.error("Failed to save: " + res.error.message);
      return;
    }
    toast.success(draft.id ? "Team updated" : "Team added");
    setEditing(null);
    reload();
  };

  const handleQuickAdd = async () => {
    if (!eventId) {
      toast.error("Save the event first");
      return;
    }
    if (!qaName.trim() || !qaDivision) {
      toast.error("Team name and division required");
      return;
    }
    const opt = divOpts.find((o) => o.value === qaDivision);
    if (!opt) return;
    await handleSave({
      ...emptyDraft(),
      team_name: qaName.trim(),
      catalog_division_id: opt.catalog_division_id,
      division_label: opt.label,
      gender: opt.gender,
      tracking_type: qaType,
      payment_status: paymentStatusFromType(qaType),
      status: "tentative",
    });
    setQaName("");
    setQaDivision("");
    setQaType("pay_at_door");
  };

  const updateRowStatus = async (
    row: EventTeamTracking,
    status: TrackingStatus
  ) => {
    if (status === "no_show") {
      const ok = window.confirm(
        "Mark as no-show?\n\nWas this team a paid registration? If so, refunds need to be handled separately."
      );
      if (!ok) return;
    }
    const { error } = await supabase
      .from("event_team_tracking")
      .update({ status })
      .eq("id", row.id);
    if (error) toast.error("Failed: " + error.message);
    else {
      toast.success("Status updated");
      reload();
    }
  };

  const handleConvert = async (row: EventTeamTracking) => {
    // TODO: full conversion to event_registrations_v2 deferred. Stub: mark as converted.
    const ok = window.confirm(
      "Convert to registration?\n\nThis will mark the tracking entry as converted. Full registration creation is not yet implemented — this is a stub that just updates the status."
    );
    if (!ok) return;
    const { error } = await supabase
      .from("event_team_tracking")
      .update({
        status: "converted",
        converted_at: new Date().toISOString(),
        // converted_registration_id: <new reg id> -- TODO when conversion flow is built
      })
      .eq("id", row.id);
    if (error) toast.error("Failed: " + error.message);
    else {
      toast.success("Marked as converted (stub)");
      reload();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("event_team_tracking")
      .delete()
      .eq("id", id);
    if (error) toast.error("Failed: " + error.message);
    else {
      toast.success("Deleted");
      setDeletingId(null);
      reload();
    }
  };

  const toggleSchedule = async (row: EventTeamTracking) => {
    const { error } = await supabase
      .from("event_team_tracking")
      .update({ include_in_schedule: !row.include_in_schedule })
      .eq("id", row.id);
    if (error) toast.error("Failed: " + error.message);
    else reload();
  };

  if (!eventId) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Save the event first, then track teams here.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold tracking-tight">TEAM TRACKING</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Track teams that aren&apos;t paying through the public registration
          flow. These teams are counted by the scheduler and get game slots.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Tracked Teams" value={stats.tracked} />
        <StatCard label="Confirmed" value={stats.confirmed} />
        <StatCard
          label="Pay-at-door pending"
          value={`$${stats.pending.toFixed(0)}`}
        />
        <StatCard label="No-shows" value={stats.noShows} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-muted/40 border border-border">
        <button
          onClick={() => setEditing(emptyDraft())}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> Add Team
        </button>
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search team name…"
              className="pl-8 h-9"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="tentative">Tentative</option>
          <option value="confirmed">Confirmed</option>
          <option value="no_show">No-show</option>
          <option value="cancelled">Cancelled</option>
          <option value="converted">Converted</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as any)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="all">All types</option>
          {TRACKING_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_META[t].label}
            </option>
          ))}
        </select>
        <select
          value={divisionFilter}
          onChange={(e) => setDivisionFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="all">All divisions</option>
          {catalog.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <ClipboardList className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No tracked teams yet. Add a comp team, pay-at-door entry, or
              invited team to get started.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Team Name</th>
                  <th className="text-left px-3 py-2">Division</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Roster</th>
                  <th className="text-left px-3 py-2">Payment</th>
                  <th className="text-center px-3 py-2">Schedule</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const tm = TYPE_META[r.tracking_type];
                  const sm = STATUS_META[r.status];
                  return (
                    <tr
                      key={r.id}
                      className={cn(
                        "border-t border-border",
                        !r.include_in_schedule && "opacity-50"
                      )}
                    >
                      <td className="px-3 py-2 font-medium">{r.team_name}</td>
                      <td className="px-3 py-2">
                        {r.division_label || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            "inline-flex px-2 py-0.5 rounded-full text-xs font-medium",
                            tm.cls
                          )}
                        >
                          {tm.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">{r.expected_roster_size ?? "—"}</td>
                      <td className="px-3 py-2">
                        {r.tracking_type === "comp" ? (
                          "—"
                        ) : (
                          <span className="text-xs">
                            ${Number(r.amount_received || 0).toFixed(0)} / $
                            {Number(r.expected_amount || 0).toFixed(0)}
                            <span className="ml-2 text-muted-foreground">
                              {r.payment_status}
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Checkbox
                          checked={r.include_in_schedule}
                          onCheckedChange={() => toggleSchedule(r)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            "inline-flex px-2 py-0.5 rounded-full text-xs font-medium",
                            sm.cls
                          )}
                        >
                          {sm.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <IconBtn
                            title="Edit"
                            onClick={() => setEditing(r)}
                          >
                            <Pencil className="w-4 h-4" />
                          </IconBtn>
                          <IconBtn
                            title="Mark No-show"
                            onClick={() => updateRowStatus(r, "no_show")}
                          >
                            <XCircle className="w-4 h-4" />
                          </IconBtn>
                          <IconBtn
                            title="Mark Cancelled"
                            onClick={() => updateRowStatus(r, "cancelled")}
                          >
                            <Ban className="w-4 h-4" />
                          </IconBtn>
                          <IconBtn
                            title="Convert to registration"
                            onClick={() => handleConvert(r)}
                          >
                            <ArrowRightCircle className="w-4 h-4" />
                          </IconBtn>
                          <IconBtn
                            title="Delete"
                            onClick={() => setDeletingId(r.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground p-3 rounded-md bg-blue-50 border border-blue-100">
        <Info className="w-4 h-4 mt-0.5 text-blue-600" />
        <span>
          Tracked teams are counted alongside registered teams when scheduling games.
        </span>
      </div>

      {/* Edit dialog */}
      {editing && (
        <TrackingEditDialog
          draft={editing}
          divisionOptions={divOpts}
          sportId={sportId}
          orgId={orgId}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {/* Delete confirm */}
      <Dialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete tracking entry?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently delete this tracking entry? This cannot be undone.
          </p>
          <DialogFooter>
            <button
              className="px-3 py-2 rounded-md border border-input text-sm"
              onClick={() => setDeletingId(null)}
            >
              Cancel
            </button>
            <button
              className="px-3 py-2 rounded-md bg-red-600 text-white text-sm"
              onClick={() => deletingId && handleDelete(deletingId)}
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="p-3 rounded-lg border border-border bg-card">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function IconBtn({
  children,
  onClick,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
    >
      {children}
    </button>
  );
}

/* ─── Edit Dialog ───────────────────────────────────────── */
function TrackingEditDialog({
  draft: initialDraft,
  divisionOptions: divOpts,
  sportId,
  orgId,
  onClose,
  onSave,
}: {
  draft: Partial<EventTeamTracking>;
  divisionOptions: { value: string; label: string; catalog_division_id: string; gender: "boys" | "girls" }[];
  sportId: string;
  orgId: string;
  onClose: () => void;
  onSave: (d: Partial<EventTeamTracking>) => Promise<void>;
}) {
  const [draft, setDraft] = useState<Partial<EventTeamTracking>>(initialDraft);
  const [saving, setSaving] = useState(false);

  // ── Club-first, two-step team picker ──
  type ClubMatch = {
    club_id: string;
    club_name: string;
    home_state: string | null;
    similarity: number;
    total_teams: number;
    teams_in_division: number;
  };
  type ClubTeam = {
    id: string;
    display_name: string | null;
    age_group: string | null;
    gender: string | null;
  };

  // Step 1: club search
  const [clubSearch, setClubSearch] = useState("");
  const [clubResults, setClubResults] = useState<ClubMatch[]>([]);
  const [searchingClubs, setSearchingClubs] = useState(false);

  // Step 2: squad selection
  const [selectedClub, setSelectedClub] = useState<ClubMatch | null>(
    initialDraft.club_id
      ? {
          club_id: initialDraft.club_id,
          club_name: initialDraft.team_name || "Selected club",
          home_state: null,
          similarity: 0,
          total_teams: 0,
          teams_in_division: 0,
        }
      : null
  );
  const [clubTeams, setClubTeams] = useState<ClubTeam[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Sub-modes
  const [newSquad, setNewSquad] = useState(false); // add a new squad under the club
  const [newClubMode, setNewClubMode] = useState(false); // create a brand-new club
  const [newClubName, setNewClubName] = useState("");
  const [newClubError, setNewClubError] = useState<string | null>(null);
  const [variation, setVariation] = useState(""); // squad label, e.g. "Elite", "Blue" → short_name

  // Detect an age group (14u/17U/18-under) or "under" inside a club name string
  const containsAgeGroup = (s: string) =>
    /\d{1,2}\s*-?\s*u\b/i.test(s) || /\bunder\b/i.test(s);

  // Strip gender words from a division label → bare age group, e.g. "17U Boys" → "17u"
  const ageGroupLabel = (lbl: string | null | undefined) =>
    (lbl || "")
      .replace(/\b(boys|girls|men|women|coed)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  // Debounced club search via find_club_matches RPC
  useEffect(() => {
    const q = clubSearch.trim();
    if (selectedClub || newClubMode || q.length < 2) {
      setClubResults([]);
      return;
    }
    let cancelled = false;
    setSearchingClubs(true);
    const handle = setTimeout(async () => {
      const { data, error } = await supabase.rpc("find_club_matches", {
        p_query: q,
        p_age_group: null,
        p_gender: null,
        p_limit: 8,
      });
      if (cancelled) return;
      setSearchingClubs(false);
      console.log("[find_club_matches]", { p_query: q, error, count: data?.length, data });
      if (error) {
        console.error("[find_club_matches] error", error);
        setClubResults([]);
        return;
      }
      setClubResults((data ?? []) as ClubMatch[]);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [clubSearch, selectedClub, newClubMode]);

  // Load a club's active squads when selected
  const pickClub = async (club: ClubMatch) => {
    setSelectedClub(club);
    setClubResults([]);
    setNewSquad(false);
    update({ club_id: club.club_id });
    setLoadingTeams(true);
    const { data, error } = await supabase
      .from("teams")
      .select("id, display_name, age_group, gender")
      .eq("club_id", club.club_id)
      .eq("status", "active")
      .order("display_name");
    setLoadingTeams(false);
    if (error) {
      console.error("[club teams] error", error);
      setClubTeams([]);
      return;
    }
    setClubTeams((data ?? []) as ClubTeam[]);
  };

  const resetClub = () => {
    setSelectedClub(null);
    setClubTeams([]);
    setNewSquad(false);
    setNewClubMode(false);
    setNewClubName("");
    update({
      club_id: null,
      master_team_id: null,
      team_name: "",
      gender: null,
      catalog_division_id: null,
      division_label: null,
    });
  };

  // Map age_group (+ gender) to an existing event division option
  const matchDivision = (ageGroup: string | null, gender: string | null) => {
    if (!ageGroup) return null;
    const ag = ageGroup.toLowerCase();
    const g = gender === "boys" || gender === "girls" ? gender : null;
    return (
      divOpts.find((o) => {
        const cat = o.label.toLowerCase();
        return cat.startsWith(ag) && (!g || o.gender === g);
      }) || null
    );
  };

  const divisionValue =
    draft.catalog_division_id && draft.gender
      ? `${draft.catalog_division_id}:${draft.gender}`
      : "";

  const update = (patch: Partial<EventTeamTracking>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const onTypeChange = (t: TrackingType) => {
    update({
      tracking_type: t,
      payment_status: paymentStatusFromType(t),
    });
  };

  const onDivisionChange = (val: string) => {
    const opt = divOpts.find((o) => o.value === val);
    if (!opt) return;
    update({
      catalog_division_id: opt.catalog_division_id,
      gender: opt.gender,
      division_label: opt.label,
    });
  };

  // Pick an existing squad (variation) under the selected club
  const pickSquad = (t: ClubTeam) => {
    const name = t.display_name || selectedClub?.club_name || "";
    const gender =
      t.gender === "boys" || t.gender === "girls"
        ? (t.gender as "boys" | "girls")
        : null;
    const matched = matchDivision(t.age_group, gender);
    update({
      master_team_id: t.id,
      team_name: name,
      gender: matched ? matched.gender : gender,
      catalog_division_id: matched ? matched.catalog_division_id : null,
      division_label: matched ? matched.label : t.age_group || null,
    });
  };

  const startNewSquad = () => {
    setNewSquad(true);
    setVariation("");
    update({
      master_team_id: null,
      short_name: null,
      team_name: selectedClub ? `${selectedClub.club_name} ` : "",
    });
  };

  // Build the full squad name from club + variation + age group while creating a new squad
  const builtSquadName = selectedClub
    ? [selectedClub.club_name, variation.trim(), ageGroupLabel(draft.division_label)]
        .filter(Boolean)
        .join(" ")
    : "";

  useEffect(() => {
    if (!newSquad || !selectedClub) return;
    update({
      short_name: variation.trim() || null,
      team_name: builtSquadName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variation, draft.division_label, newSquad, selectedClub]);

  const startNewClub = () => {
    setNewClubMode(true);
    setSelectedClub(null);
    setClubTeams([]);
    setClubResults([]);
  };

  const confirmNewClub = () => {
    const nm = newClubName.trim();
    if (!nm) {
      setNewClubError("Club name is required");
      return;
    }
    if (containsAgeGroup(nm)) {
      setNewClubError(
        "Club name must be the base program only — remove the age group (e.g. use 'Swing', not 'Swing 14u')."
      );
      return;
    }
    setNewClubError(null);
    // No club record is created yet; capture the name and move to squad details.
    setSelectedClub({
      club_id: "",
      club_name: nm,
      home_state: null,
      similarity: 0,
      total_teams: 0,
      teams_in_division: 0,
    });
    setNewClubMode(false);
    setNewSquad(true);
    setVariation("");
    update({
      club_id: null,
      master_team_id: null,
      short_name: null,
      team_name: `${nm} `,
      _newClubName: nm,
    } as any);
  };


  const submit = async () => {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
  };

  const isComp = draft.tracking_type === "comp";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{draft.id ? "Edit team" : "Add tracked team"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Basics */}
          <Section title="Basics">
            {/* Step 1: search clubs */}
            {!selectedClub && !newClubMode && (
              <div className="relative">
                <Label>Search club *</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                  {searchingClubs && (
                    <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                  <Input
                    className="pl-8"
                    value={clubSearch}
                    onChange={(e) => setClubSearch(e.target.value)}
                    placeholder="Type a club name (e.g. Bears)…"
                  />
                </div>
                {clubSearch.trim().length >= 2 && (
                  <div className="mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover shadow-sm">
                    {clubResults.length === 0 && !searchingClubs ? (
                      <div className="px-3 py-3 text-sm text-muted-foreground">
                        No clubs found.
                      </div>
                    ) : (
                      clubResults.map((c) => (
                        <button
                          key={c.club_id}
                          type="button"
                          onClick={() => pickClub(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                        >
                          <span className="font-medium">{c.club_name}</span>
                          {c.home_state ? ` — ${c.home_state}` : ""}{" "}
                          <span className="text-xs text-muted-foreground">
                            ({c.total_teams} team{c.total_teams === 1 ? "" : "s"})
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={startNewClub}
                  className="text-xs text-primary hover:underline mt-2"
                >
                  Can&apos;t find the club? Create new club
                </button>
              </div>
            )}

            {/* Create new club */}
            {newClubMode && (
              <div className="space-y-2">
                <Label>New club name *</Label>
                <Input
                  value={newClubName}
                  onChange={(e) => {
                    setNewClubName(e.target.value);
                    if (newClubError) setNewClubError(null);
                  }}
                  placeholder="Swing, Bears, Wisconsin Swing…"
                  aria-invalid={!!newClubError}
                />
                <p className="text-xs text-muted-foreground">
                  Base program name only — no age group or variation. e.g.
                  &quot;Swing&quot;, not &quot;Swing 14u&quot;.
                </p>
                {newClubError && (
                  <p className="text-xs text-destructive">{newClubError}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={confirmNewClub}
                    className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm"
                  >
                    Continue
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewClubMode(false);
                      setNewClubName("");
                      setNewClubError(null);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    ← Back to club search
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: pick / add squad under the club */}
            {selectedClub && (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-md bg-muted/50 border border-border px-3 py-2">
                  <div className="text-sm">
                    <span className="font-semibold">{selectedClub.club_name}</span>
                    {selectedClub.home_state ? ` · ${selectedClub.home_state}` : ""}
                  </div>
                  <button
                    type="button"
                    onClick={resetClub}
                    className="text-xs text-primary hover:underline"
                  >
                    Change club
                  </button>
                </div>

                {!newSquad ? (
                  <div>
                    <Label>Select squad *</Label>
                    {loadingTeams ? (
                      <div className="px-3 py-3 text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading squads…
                      </div>
                    ) : (
                      <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border">
                        {clubTeams.length === 0 ? (
                          <div className="px-3 py-3 text-sm text-muted-foreground">
                            No active squads yet — add one below.
                          </div>
                        ) : (
                          clubTeams.map((t) => {
                            const name = t.display_name || "Unnamed squad";
                            const meta = [t.age_group, t.gender]
                              .filter(Boolean)
                              .join(" · ");
                            const isSel = draft.master_team_id === t.id;
                            return (
                              <button
                                key={t.id}
                                type="button"
                                onClick={() => pickSquad(t)}
                                className={cn(
                                  "w-full text-left px-3 py-2 text-sm hover:bg-accent",
                                  isSel && "bg-primary/10"
                                )}
                              >
                                <div className="font-medium">{name}</div>
                                {meta && (
                                  <div className="text-xs text-muted-foreground">
                                    {meta}
                                  </div>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                    {draft.master_team_id && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Selected: <strong>{draft.team_name}</strong>
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={startNewSquad}
                      className="text-xs text-primary hover:underline mt-2"
                    >
                      + Add a new squad
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setNewSquad(false);
                      setVariation("");
                      update({ short_name: null, master_team_id: null });
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    ← Back to squad list
                  </button>
                )}
              </div>

            )}


            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Division *</Label>
                <select
                  value={divisionValue}
                  onChange={(e) => onDivisionChange(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="">Select…</option>
                  {divOpts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {!divisionValue && draft.division_label && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Using “{draft.division_label}” — pick a matching event division
                    above to link it.
                  </p>
                )}
              </div>
              <div>
                <Label>Expected Roster Size</Label>
                <Input
                  type="number"
                  value={draft.expected_roster_size ?? ""}
                  onChange={(e) =>
                    update({
                      expected_roster_size: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                />
              </div>
            </div>

            {selectedClub && newSquad && (
              <div className="space-y-2">
                <Label>Variation / Squad label (optional)</Label>
                <Input
                  value={variation}
                  onChange={(e) => setVariation(e.target.value)}
                  placeholder="Elite, Blue, Red, Noll…"
                />
                <p className="text-xs text-muted-foreground">
                  Maps to the squad label. Leave blank for an unnamed squad.
                </p>
                <div className="rounded-md bg-muted/50 border border-border px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Full squad name
                  </div>
                  <div className="text-sm font-semibold">
                    {builtSquadName || (
                      <span className="text-muted-foreground font-normal">
                        {selectedClub.club_name} … (add a label &amp; division)
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  The squad will be created under {selectedClub.club_name} as{" "}
                  <strong>{selectedClub.club_name} {variation.trim() || "<label>"} {ageGroupLabel(draft.division_label) || "<age>"}</strong>.
                </p>
              </div>
            )}
          </Section>


          {/* Type & status */}
          <Section title="Type & Status">
            <div>
              <Label>Tracking Type *</Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
                {TRACKING_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => onTypeChange(t)}
                    className={cn(
                      "p-2 rounded-md border text-xs font-medium transition",
                      draft.tracking_type === t
                        ? "border-primary bg-primary/10"
                        : "border-input hover:bg-accent"
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block w-2 h-2 rounded-full mr-1.5",
                        TYPE_META[t].chip
                      )}
                    />
                    {TYPE_META[t].label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Status *</Label>
              <div className="flex gap-2 mt-1">
                {(["tentative", "confirmed"] as TrackingStatus[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => update({ status: s })}
                    className={cn(
                      "px-3 py-1.5 rounded-md border text-sm",
                      draft.status === s
                        ? "border-primary bg-primary/10"
                        : "border-input"
                    )}
                  >
                    {STATUS_META[s].label}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          {/* Contact */}
          <Section title="Contact">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={draft.contact_name || ""}
                  onChange={(e) => update({ contact_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={draft.contact_email || ""}
                  onChange={(e) => update({ contact_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  value={draft.contact_phone || ""}
                  onChange={(e) => update({ contact_phone: e.target.value })}
                />
              </div>
            </div>
          </Section>

          {/* Payment */}
          <Section title="Payment">
            {isComp ? (
              <p className="text-xs text-muted-foreground">
                Comp teams don&apos;t require payment.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Expected Amount ($)</Label>
                    <Input
                      type="number"
                      value={draft.expected_amount ?? 0}
                      onChange={(e) =>
                        update({ expected_amount: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div>
                    <Label>Amount Received ($)</Label>
                    <Input
                      type="number"
                      value={draft.amount_received ?? 0}
                      onChange={(e) =>
                        update({ amount_received: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div>
                    <Label>Payment Status</Label>
                    <select
                      value={draft.payment_status || "pending"}
                      onChange={(e) =>
                        update({
                          payment_status: e.target.value as TrackingPaymentStatus,
                        })
                      }
                      className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="not_required">Not required</option>
                      <option value="pending">Pending</option>
                      <option value="partial">Partial</option>
                      <option value="paid">Paid</option>
                      <option value="waived">Waived</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                  <div>
                    <Label>Payment Method</Label>
                    <Input
                      value={draft.payment_method || ""}
                      onChange={(e) => update({ payment_method: e.target.value })}
                      placeholder="cash, check, Venmo…"
                    />
                  </div>
                </div>
                <div>
                  <Label>Payment Notes</Label>
                  <textarea
                    rows={2}
                    value={draft.payment_notes || ""}
                    onChange={(e) => update({ payment_notes: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
              </>
            )}
          </Section>

          {/* Scheduling */}
          <Section title="Scheduling">
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={draft.include_in_schedule ?? true}
                onCheckedChange={(v) => update({ include_in_schedule: !!v })}
                className="mt-0.5"
              />
              <span>
                Include in scheduler
                <span className="block text-xs text-muted-foreground">
                  Uncheck if this team is informational only and shouldn&apos;t get
                  game slots.
                </span>
              </span>
            </label>
          </Section>

          {/* Notes */}
          <Section title="Notes">
            <div>
              <Label>Notes</Label>
              <textarea
                rows={2}
                value={draft.notes || ""}
                onChange={(e) => update({ notes: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Visible to anyone with team access"
              />
            </div>
            <div>
              <Label>Internal Notes</Label>
              <textarea
                rows={2}
                value={draft.internal_notes || ""}
                onChange={(e) => update({ internal_notes: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Operator-only — never shown to teams or partners"
              />
            </div>
          </Section>
        </div>

        <DialogFooter>
          <button
            className="px-3 py-2 rounded-md border border-input text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            disabled={saving}
            onClick={submit}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      {children}
    </div>
  );
}
